import { useEffect } from "react";
import { capture } from "@/lib/posthog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiFetch, put } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { useAuth } from "@/lib/auth";
import { API_BASE } from "@/lib/utils";
import type { EntitlementKey } from "@/lib/entitlements/modules";

/** Shown wherever a workflow code write is refused because the org is read-only.
 *  One string so the editor chip, the Publish/Deploy tooltips and any future
 *  surface cannot describe the same restriction three different ways. The
 *  second-person form of the server's CODE_READ_ONLY_MESSAGE. */
export const CODE_READ_ONLY_HINT =
  "Your organization can read workflow code but not change it.";

// ── Types ────────────────────────────────────────────────────────────────────

export type ResolvedEntitlements = Record<EntitlementKey, boolean>;

export interface PlatformEntitlementRow {
  value: boolean;
  isSet: boolean;
}
export type PlatformEntitlements = Record<EntitlementKey, PlatformEntitlementRow>;

export interface OrgEntitlementRow {
  override: boolean | null;
  effective: boolean;
  source: "override" | "platform" | "safe_default";
}
export type OrgEntitlements = Record<EntitlementKey, OrgEntitlementRow>;

// ── Caller-facing hooks ──────────────────────────────────────────────────────

/**
 * Resolved entitlements for the current user's org. One batched fetch covers
 * every key — `useEntitlement(key)` reads off this query.
 *
 * Stale time = 60 s to match the backend's 60 s TTL cache on
 * `getResolvedEntitlements`. The admin save mutation invalidates the *admin's*
 * own client, but other users in the affected org sit on the cache until it
 * goes stale — so this number is also the worst-case lag they see between
 * a flip and their UI reflecting it. `refetchOnWindowFocus` shortens that
 * further: any tab the user comes back to refetches immediately if stale.
 * KAN-1880.
 */
export function useResolvedEntitlements() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: keys.entitlements.me(orgId),
    queryFn: () =>
      apiFetch<ResolvedEntitlements>(`${API_BASE}/v1/me/entitlements`),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Keys whose server-side SAFE_DEFAULT is TRUE (packages/rbac `SAFE_DEFAULTS`).
 *
 * `/me/entitlements` resolves every key the *API* knows about, so in a rolling
 * deploy where this bundle is ahead of the API — or a cached bundle hitting an
 * older API — a newly-added key is simply ABSENT from the response, not `false`.
 * Reading absent as `false` would flip the capability OFF for every org, the
 * exact opposite of the server default; for `workflow.codeEditor.write` that
 * silently demotes every code-editing org to read-only. So mirror the server:
 * an absent key resolves to its safe default. Only keys that default TRUE need
 * listing — everything else is hide-by-default, for which absent ⇒ false is
 * already correct.
 */
const DEFAULT_TRUE_WHEN_ABSENT: ReadonlySet<EntitlementKey> = new Set<EntitlementKey>([
  "workflow.codeEditor.write",
  // Help text, not a capability anyone buys. The key exists so it can be
  // switched off in prod without a deploy, so absent has to mean on — matching
  // safeDefault() in @fastn/rbac.
  "guidance.pageGuide",
]);

/**
 * Single-key reader. Returns `{ enabled, isLoading }`.
 *
 * Default to `false` while loading so the gated button stays hidden until we
 * know — matches the hide-by-default policy and avoids a flash of the button
 * for orgs that don't have it enabled.
 */
export function useEntitlement(
  key: EntitlementKey,
  /** `report: false` for a caller that only reads the answer ahead of any gate —
   *  a page that is not itself behind this entitlement would otherwise log an
   *  `entitlement_blocked` on every visit by an org without it. */
  opts: { report?: boolean } = {},
): { enabled: boolean; isLoading: boolean; isError: boolean } {
  const report = opts.report ?? true;
  const { contextPending } = useAuth();
  const { data, isLoading, isError } = useResolvedEntitlements();
  // `data?.[key]` is undefined only when the API omitted the key (a real value
  // is always boolean), so undefined ⇒ the key is absent ⇒ fall to its default.
  const raw = data?.[key];
  const enabled = raw === undefined
    ? Boolean(data) && DEFAULT_TRUE_WHEN_ABSENT.has(key)
    : raw === true;
  // `useResolvedEntitlements` is disabled until orgId resolves, and React Query
  // reports isLoading:false for a disabled query — so on a brand-new account
  // this looked like a settled "not entitled" answer rather than "not asked
  // yet", and EntitlementGate redirected the owner off /agent before their org
  // had finished provisioning.
  // `isError` is surfaced so callers can tell "your org does not have this"
  // from "we could not find out". A gate that conflates them turns any 401 or
  // 500 on this one request into a silent redirect off the page.
  const settled = !(isLoading || contextPending) && !isError;

  // A commercial wall, not a technical one: this is a feature the org's plan
  // does not include. Counted only once the answer is SETTLED — reporting the
  // loading state as a block would fire on every page load for every user and
  // make the gate look like it denies everyone.
  useEffect(() => {
    if (!report || !settled || enabled) return;
    capture("entitlement_blocked", {
      entitlement_key: key,
      route: window.location.pathname,
      plan: null,
    });
  }, [report, settled, enabled, key]);

  return { enabled, isLoading: isLoading || contextPending, isError };
}

// ── Admin-facing hooks (platform_admin only) ─────────────────────────────────

export function usePlatformEntitlements() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: keys.entitlements.platform(orgId),
    queryFn: () =>
      apiFetch<PlatformEntitlements>(`${API_BASE}/v1/admin/entitlements`),
    enabled: Boolean(orgId),
  });
}

export function useUpdatePlatformEntitlements() {
  return useMutation({
    mutationFn: (entitlements: Partial<Record<EntitlementKey, boolean>>) =>
      apiFetch<{ entitlements: Partial<Record<EntitlementKey, boolean>> }>(
        `${API_BASE}/v1/admin/entitlements`,
        put({ entitlements }),
      ),
    onSuccess: (_data, _vars, _ctx) => {
      // Bust both /me and /admin views — the platform save may flip the
      // effective value for any org without an override.
      queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    },
  });
}

export function useOrgEntitlements(targetOrgId: string | null) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: keys.entitlements.org(orgId, targetOrgId ?? undefined),
    queryFn: () =>
      apiFetch<{ orgId: string; entitlements: OrgEntitlements }>(
        `${API_BASE}/v1/admin/entitlements/orgs/${targetOrgId}`,
      ),
    enabled: Boolean(orgId) && Boolean(targetOrgId),
  });
}

// ── Matrix view (KAN-1789) ───────────────────────────────────────────────────

export interface MatrixFeature {
  key: EntitlementKey;
  defaultValue: boolean;
  defaultSource: "platform" | "safe_default";
  counts: { enabled: number; disabled: number; override: number };
}
export interface MatrixOrg {
  orgId: string;
  orgName: string;
  orgType: string;
  values: Record<EntitlementKey, { value: boolean; source: "override" | "platform" | "safe_default" }>;
}
export interface EntitlementsMatrix {
  features: MatrixFeature[];
  orgs: MatrixOrg[];
}

/**
 * One-shot fetch of every org's effective value for every known entitlement.
 * Powers /admin/entitlements/browser. Server-side aggregation keeps payload
 * small (~ ENTITLEMENT_KEYS × org_count booleans) so this scales fine to
 * thousands of orgs and tens of entitlements before we'd need to paginate.
 */
export function useEntitlementsMatrix() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: keys.entitlements.matrix(orgId),
    queryFn: () =>
      apiFetch<EntitlementsMatrix>(`${API_BASE}/v1/admin/entitlements/matrix`),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useUpdateOrgEntitlements() {
  return useMutation({
    mutationFn: ({ orgId, entitlements }: { orgId: string; entitlements: Partial<Record<EntitlementKey, boolean>> }) =>
      apiFetch<{ orgId: string; entitlements: Partial<Record<EntitlementKey, boolean>> }>(
        `${API_BASE}/v1/admin/entitlements/orgs/${orgId}`,
        put({ entitlements }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    },
  });
}
